import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from './supabaseClient';
import { trackPageView, trackSessionStart, updateSessionDuration } from './analyticsService';
import SEO from './SEO';
import LanguageSwitcher from './LanguageSwitcher';
import './Reveel.css';

// Lazy load components for better performance
const HowItWorks = lazy(() => import('./HowItWorks'));
const Team = lazy(() => import('./Team'));
const Reviews = lazy(() => import('./Reviews'));

const Reveel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [blogs, setBlogs] = useState([]);
  const [formStatus, setFormStatus] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Analytics tracking
  useEffect(() => {
    trackSessionStart();
    trackPageView('/');

    // Update session duration when user leaves
    const handleBeforeUnload = () => {
      updateSessionDuration();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Detect which section is currently in view
      const sections = ['home', 'how-it-works', 'team', 'blog', 'reviews', 'contacts'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Run once on mount to set initial section
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Restore scroll position when returning from blog detail
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('reveelScrollPosition');
    if (savedScrollPosition) {
      // Use setTimeout to ensure the page is fully rendered
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
      }, 100);
      // Clear the saved position after restoring
      sessionStorage.removeItem('reveelScrollPosition');
    }
  }, []);

  const fetchBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBlogClick = (blog) => {
    // Save scroll position before navigating
    sessionStorage.setItem('reveelScrollPosition', window.scrollY.toString());
    // Use slug if available, otherwise fallback to ID
    const identifier = blog.slug || blog.id;
    navigate(`/blog/${identifier}`);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await fetch('https://formspree.io/f/xqajgary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setFormStatus('error');
    }
  };

  return (
    <div className="reveel-app">
      <SEO />
      {/* Navigation Bar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="logo">
            <img
              src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/reveel-icon-v2.png"
              alt="Reveel Logo"
              className="logo-image"
              loading="eager"
            />
          </div>

          <div className="nav-menu">
            <a
              href="#home"
              className={`nav-item ${activeSection === 'home' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
            >
              {t('nav.home')}
            </a>
            <a
              href="#how-it-works"
              className={`nav-item ${activeSection === 'how-it-works' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}
            >
              {t('nav.howItWorks')}
            </a>
            <a
              href="#team"
              className={`nav-item ${activeSection === 'team' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('team'); }}
            >
              {t('nav.team')}
            </a>
            <a
              href="#reviews"
              className={`nav-item ${activeSection === 'reviews' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}
            >
              {t('nav.testimonials')}
            </a>
            <a
              href="#blog"
              className={`nav-item ${activeSection === 'blog' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('blog'); }}
            >
              {t('nav.blog')}
            </a>
            <a
              href="#contacts"
              className={`nav-item ${activeSection === 'contacts' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollToSection('contacts'); }}
            >
              {t('nav.contacts')}
            </a>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`mobile-menu-button ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* <div className="nav-actions">
            <button className="btn-outline">TRY FREE</button>
          </div> */}
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a
            href="#home"
            className={`mobile-nav-item ${activeSection === 'home' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
          >
            {t('nav.home')}
          </a>
          <a
            href="#how-it-works"
            className={`mobile-nav-item ${activeSection === 'how-it-works' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}
          >
            {t('nav.howItWorks')}
          </a>
          <a
            href="#team"
            className={`mobile-nav-item ${activeSection === 'team' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('team'); }}
          >
            {t('nav.team')}
          </a>
          <a
            href="#reviews"
            className={`mobile-nav-item ${activeSection === 'reviews' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}
          >
            {t('nav.testimonials')}
          </a>
          <a
            href="#blog"
            className={`mobile-nav-item ${activeSection === 'blog' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('blog'); }}
          >
            {t('nav.blog')}
          </a>
          <a
            href="#contacts"
            className={`mobile-nav-item ${activeSection === 'contacts' ? 'active' : ''}`}
            onClick={(e) => { e.preventDefault(); scrollToSection('contacts'); }}
          >
            {t('nav.contacts')}
          </a>
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Social Sidebar */}
      <div className="social-sidebar">
        <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-item">
          {t('social.twitter')}
        </a>
        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-item">
          {t('social.instagram')}
        </a>
      </div>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-left">
            <p className="category-label">{t('hero.tag')}</p>
            <h1 className="hero-heading">
              <span className="heading-line">{t('hero.title1')}</span>
              <span className="heading-line">{t('hero.title2')}</span>
              <span className="heading-line gradient-text">{t('hero.title3')}</span>
            </h1>
            <p className="hero-description">
              {t('hero.subtitle1')}<br />
              {t('hero.subtitle2')}<br />
              {t('hero.description')}
            </p>

            {/* Platform Badges */}
            <div className="platform-badges">
              <div className="platform-badge">
                <svg className="platform-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="badge-text">
                  <span className="badge-label">{t('hero.comingSoon')}</span>
                  <span className="badge-title">{t('hero.ios')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-right">
            <div className="gradient-sphere-container">
              <div className="gradient-sphere">
                <div className="sphere-layer layer-1"></div>
                <div className="sphere-layer layer-2"></div>
                <div className="sphere-layer layer-3"></div>
              </div>
              <div className="sphere-glow"></div>
              <p className="track-badge">
                {t('hero.trackTitle1')}<br />
                {t('hero.trackTitle2')}<br />
                {t('hero.trackTitle3')}
              </p>
            </div>

            {/* iPhone Mockup */}
            <div className="iphone-mockup-container">
              <div className="iphone-glow"></div>
              <div className="iphone-frame">
                <div className="iphone-screen">
                  <div className="iphone-notch"></div>
                  <img
                    src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/reveel/IMG_2007.PNG"
                    alt="Reveel App Preview"
                    className="iphone-screen-content"
                    loading="eager"
                    decoding="async"
                  />
                  <div className="iphone-reflection"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-number">{t('hero.statsNumber')}</div>
            <p className="stats-description">
              {t('hero.statsText')}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <Suspense fallback={<div className="section" style={{ minHeight: '500px' }}></div>}>
        <HowItWorks />
      </Suspense>

      {/* Team Section */}
      <Suspense fallback={<div className="section" style={{ minHeight: '500px' }}></div>}>
        <Team />
      </Suspense>

      {/* Reviews Section */}
      <Suspense fallback={<div className="section" style={{ minHeight: '500px' }}></div>}>
        <Reviews />
      </Suspense>

      {/* Blog Section */}
      <section id="blog" className="section blog-section">
        <div className="section-container">
          <h2 className="section-title">{t('blog.tag')}</h2>
          <div className="blog-grid">
            {blogs.length > 0 ? (
              blogs.map((blog) => (
                <article
                  key={blog.id}
                  className="blog-card"
                  onClick={() => handleBlogClick(blog)}
                  style={{ cursor: 'pointer' }}
                >
                  {blog.image_url ? (
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="blog-image"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="blog-image"></div>
                  )}
                  <h3>{blog.title}</h3>
                  <p>{blog.description}</p>
                  <span className="read-more">{t('blog.readMore')}</span>
                </article>
              ))
            ) : (
              <>
                <article className="blog-card">
                  <div className="blog-image"></div>
                  <h3>{t('blog.fallback1.title')}</h3>
                  <p>{t('blog.fallback1.description')}</p>
                  <span className="read-more">{t('blog.readMore')}</span>
                </article>
                <article className="blog-card">
                  <div className="blog-image"></div>
                  <h3>{t('blog.fallback2.title')}</h3>
                  <p>{t('blog.fallback2.description')}</p>
                  <span className="read-more">{t('blog.readMore')}</span>
                </article>
                <article className="blog-card">
                  <div className="blog-image"></div>
                  <h3>{t('blog.fallback3.title')}</h3>
                  <p>{t('blog.fallback3.description')}</p>
                  <span className="read-more">{t('blog.readMore')}</span>
                </article>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacts" className="contact-section">
        {/* Floating Particles */}
        <div className="contact-particles">
          <div className="contact-particle cp1"></div>
          <div className="contact-particle cp2"></div>
          <div className="contact-particle cp3"></div>
        </div>

        <div className="contact-wrapper">
          {/* Header */}
          <div className="contact-heading">
            <p className="contact-tag">{t('contact.tag')}</p>
            <h2 className="contact-main-title">
              <span className="title-dark">{t('contact.title1')}</span>
              <span className="title-color">{t('contact.title2')}</span>
            </h2>
            <p className="contact-subtitle">
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Contact Grid */}
          <div className="contact-grid">
            {/* Info Cards */}
            <div className="contact-info-section">
              {/* Email Card */}
              <div className="info-card">
                <div className="info-glow glow-purple"></div>
                <div className="info-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="info-title">{t('contact.email')}</h3>
                <p className="info-detail">hello@reveel.health</p>
                <p className="info-desc">{t('contact.emailSubtext')}</p>
              </div>

              {/* Location Card */}
              <div className="info-card">
                <div className="info-glow glow-cyan"></div>
                <div className="info-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="info-title">{t('contact.address')}</h3>
                <p className="info-detail">231 Avenue du Faubourg Saint-Honoré</p>
                <p className="info-desc">Paris 75001, France</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-container">
                <div className="form-glow"></div>
                <form className="contact-form" onSubmit={handleContactSubmit}>
                  <div className="form-group">
                    <label className="form-label">{t('contact.form.name')}</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('contact.form.namePlaceholder')}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('contact.form.email')}</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder={t('contact.form.emailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('contact.form.message')}</label>
                    <textarea
                      className="form-textarea"
                      placeholder={t('contact.form.messagePlaceholder')}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                    ></textarea>
                  </div>

                  {formStatus === 'success' && (
                    <div className="form-success">
                      {t('contact.form.success')}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="form-error">
                      {t('contact.form.error')}
                    </div>
                  )}

                  <button type="submit" className="form-submit-btn" disabled={formStatus === 'sending'}>
                    {formStatus === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
                    <svg className="btn-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-logo">
            <img
              src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/reveel-icon-v2.png"
              alt="Reveel Logo"
              className="logo-image"
              loading="lazy"
            />
            <span>Reveel</span>
          </div>
          <p className="footer-text">
            {t('footer.copyright')}
          </p>
          <div className="footer-links">
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="footer-link">Terms & Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Reveel;